/**
 * CompositeActionIcon.jsx
 * Pure SVG composition combining a base canonical domain entity glyph (scaled 86%, lower-left)
 * with an upper-right action modifier badge (+, filter, search, etc.)
 * backed by an SVG negative-space knockout moat (r=6.0 moat, r=4.5 badge).
 *
 * Guarantees 100% compatibility with Radix UI, Lucide props (icon={Icon}),
 * and Tailwind's [&_svg]:size-4 selector by emitting a single pure <svg> element.
 */

import * as React from "react";
import { cn } from "../lib/utils.js";
import { iconRegistry } from "./registry.js";
import { initializeRegistry } from "./index.js";
import { domainMap, getDomainConfig } from "./domainMap.js";

export const CompositeActionIcon = React.forwardRef(
  (
    {
      name,
      action = "add",
      variant = "spatial-bento-duotone",
      size = 24,
      glow = false,
      color = "currentColor",
      colorClass = "",
      badgeClassName = "",
      className = "",
      style,
      "aria-label": ariaLabel,
      "aria-hidden": ariaHidden,
      ...props
    },
    ref
  ) => {
    const rawId = React.useId();
    const maskId = `homy-icon-mask-${rawId.replace(/[^a-zA-Z0-9_-]/g, "")}`;

    // Ensure default glyphs and badges are registered in production bundles
    initializeRegistry();

    // Resolve base glyph component
    const BaseGlyph = iconRegistry.getGlyph(name, variant);

    // Resolve action badge component
    const BadgeGlyph = iconRegistry.getBadge(action);

    const isSolid = variant === "obsidian-solid";

    // Resolve color: distinguish between Tailwind classes and raw CSS color values
    let resolvedColorClass = colorClass;
    let customColorStyle = null;

    if (!resolvedColorClass && color === "semantic") {
      const config = getDomainConfig(name) || iconRegistry.getDomainConfig(name);
      if (config) {
        resolvedColorClass = config.colorClass;
      }
    } else if (color === "current" || color === "currentColor") {
      resolvedColorClass = "text-current";
    } else if (color && color !== "semantic" && !resolvedColorClass) {
      if (typeof color === "string" && color.startsWith("text-")) {
        resolvedColorClass = color;
      } else {
        customColorStyle = { color };
      }
    }

    const mergedStyle = customColorStyle
      ? { ...customColorStyle, ...style }
      : style;

    const numericSize = typeof size === "number" ? size : 24;

    return (
      <svg
        ref={ref}
        viewBox="0 0 24 24"
        width={numericSize}
        height={numericSize}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={mergedStyle}
        className={cn(
          "shrink-0 select-none overflow-visible transition-colors",
          resolvedColorClass,
          glow && "filter drop-shadow-[0_0_5px_currentColor]",
          className
        )}
        data-composite={`${name}:${action}`}
        data-icon={name}
        aria-hidden={ariaHidden ?? !ariaLabel}
        aria-label={ariaLabel}
        xmlns="http://www.w3.org/2000/svg"
        {...props}
      >
        <defs>
          {/* Negative space knockout mask: prevents base icon lines from colliding with badge */}
          <mask id={maskId}>
            <rect width="24" height="24" fill="white" />
            {/* Compact circular moat around the upper-right modifier glyph */}
            <circle cx="18.5" cy="5.5" r="4.5" fill="black" />
            <circle cx="20" cy="5.25" r="4.8" fill="black" />
          </mask>
        </defs>

        {/* Base Entity Glyph: Maximized scale to fill the 24x24 viewBox with minimal offset */}
        <g
          mask={`url(#${maskId})`}
          transform="translate(-0.5, 0.5) scale(0.95)"
          fill={isSolid ? "currentColor" : undefined}
          stroke={isSolid ? "none" : undefined}
          className="transition-colors"
        >
          {BaseGlyph ? (
            <BaseGlyph embedded />
          ) : (
            /* Fallback generic box if base glyph not found */
            <rect x="2" y="2" width="20" height="20" rx="2.5" />
          )}
        </g>

        {/* Action Modifier Badge in the same schema color as the base icon */}
        <g
          className={cn(
            "transition-transform duration-150",
            badgeClassName
          )}
        >
          {/* Modifier Icon Glyph inside badge (uses currentColor to match schema) */}
          {BadgeGlyph ? (
            <BadgeGlyph className="text-current" />
          ) : (
            /* Default Plus (+) badge if no custom badge glyph registered */
            <path
              d="M18.5 2.7v5.6M15.7 5.5h5.6"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}
        </g>
      </svg>
    );
  }
);

CompositeActionIcon.displayName = "CompositeActionIcon";
